import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { adminAPI } from '../../lib/adminAPI';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { Shield, LayoutDashboard, Users, ArrowDownToLine, ArrowUpToLine, ShieldCheck, LogOut, Home, Key, Wallet, Menu, UserCog, ScrollText, Sliders, Mail } from 'lucide-react';

const NAV = [
  { to: '/admin', end: true, icon: LayoutDashboard, label: 'Overview' },
  { to: '/admin/users', icon: Users, label: 'Users' },
  { to: '/admin/deposits', icon: ArrowDownToLine, label: 'Deposits' },
  { to: '/admin/withdrawals', icon: ArrowUpToLine, label: 'Withdrawals' },
  { to: '/admin/deposit-methods', icon: Wallet, label: 'Deposit Methods' },
  { to: '/admin/kyc', icon: ShieldCheck, label: 'KYC Verification' },
  { to: '/admin/admins', icon: UserCog, label: 'Admins' },
  { to: '/admin/audit', icon: ScrollText, label: 'Audit Log' },
  { to: '/admin/settings', icon: Sliders, label: 'Trading Settings' },
  { to: '/admin/email-alerts', icon: Mail, label: 'Email Alerts' }
];

const AdminGate = ({ children }) => {
  const [status, setStatus] = useState('loading'); // loading | not_admin | admin
  const [secret, setSecret] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const check = async () => {
    try {
      const r = await adminAPI.me();
      setStatus(r.is_admin ? 'admin' : 'not_admin');
    } catch { setStatus('not_admin'); }
  };
  useEffect(() => { check(); }, []);

  const claim = async (e) => {
    e.preventDefault();
    if (!secret) return;
    setLoading(true);
    try {
      await adminAPI.claim(secret);
      toast({ title: 'Admin access granted', description: 'You are now an admin.' });
      setStatus('admin');
    } catch (err) {
      toast({ title: 'Access denied', description: err?.response?.data?.detail || 'Invalid secret' });
    } finally { setLoading(false); }
  };

  if (status === 'loading') return <div className="min-h-screen bg-[#050807] flex items-center justify-center text-[#00e676] text-sm">Verifying admin access…</div>;
  if (status === 'admin') return children;

  return (
    <div className="min-h-screen bg-[#050807] hero-glow flex items-center justify-center px-6">
      <div className="w-full max-w-md p-8 rounded-2xl border border-[#1a2823] bg-[#0a1210]">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-lg bg-[#00e676]/10 border border-[#00e676]/30 flex items-center justify-center text-[#00e676]"><Shield size={22}/></div>
          <div>
            <h1 className="text-xl font-bold text-white">Admin Access</h1>
            <p className="text-xs text-gray-400">Enter the admin secret to unlock the CRM dashboard.</p>
          </div>
        </div>
        <form onSubmit={claim} className="space-y-3">
          <div>
            <label className="text-[10px] text-gray-400 uppercase tracking-wider">Admin Secret</label>
            <div className="mt-1 relative">
              <Key size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"/>
              <input type="password" value={secret} onChange={e => setSecret(e.target.value)} placeholder="Paste admin secret…" className="w-full bg-[#050807] border border-[#1a2823] rounded-lg pl-9 pr-4 py-3 text-white text-sm focus:outline-none focus:border-[#00e676]"/>
            </div>
          </div>
          <button type="submit" disabled={loading || !secret} className="btn-green w-full py-2.5 rounded-lg font-semibold text-sm disabled:opacity-50">{loading ? 'Verifying…' : 'Claim Admin Access'}</button>
        </form>
        <Link to="/dashboard" className="block text-center mt-4 text-xs text-gray-400 hover:text-[#00e676]">← Back to trader dashboard</Link>
      </div>
    </div>
  );
};

const AdminLayout = () => {
  const nav = useNavigate();
  const loc = useLocation();
  const { user, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

  const doLogout = () => { logout(); nav('/'); };

  const SidebarBody = (
    <>
      <div className="px-6 py-5 border-b border-[#141f1b]">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#00e676] flex items-center justify-center">
            <Shield size={16} className="text-black"/>
          </div>
          <div>
            <div className="text-sm font-extrabold text-white whitespace-nowrap">NEXUS FX <span className="text-[#00e676]">PRO</span> Admin</div>
            <div className="text-[10px] text-[#00e676] uppercase tracking-wider">CRM Dashboard</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.map(n => {
          const active = n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
          return (
            <Link key={n.to} to={n.to} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${active ? 'bg-[#00e676]/10 text-[#00e676] border border-[#00e676]/20' : 'text-gray-400 hover:text-white hover:bg-[#0a1210]'}`}>
              <n.icon size={16}/> {n.label}
            </Link>
          );
        })}
        <div className="pt-3 mt-3 border-t border-[#141f1b] space-y-1">
          <Link to="/dashboard" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-[#0a1210]">
            <Home size={16}/> Trader View
          </Link>
        </div>
      </nav>
      <div className="p-4 border-t border-[#141f1b]">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-[#00e676]/15 border border-[#00e676]/30 flex items-center justify-center text-[#00e676] font-bold text-sm">
            {user?.name?.[0]?.toUpperCase() || 'A'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
            <div className="text-[11px] text-gray-400 truncate" data-testid="admin-sidebar-email">{user?.email}</div>
            <div className="text-[10px] text-[#00e676] font-bold uppercase tracking-wider">Admin</div>
          </div>
        </div>
        <button onClick={doLogout} className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-[#1a2823] text-gray-400 hover:text-red-400 hover:border-red-500/40 text-sm">
          <LogOut size={14}/> Sign out
        </button>
      </div>
    </>
  );

  return (
    <AdminGate>
      <div className="min-h-screen bg-[#050807] flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex w-64 bg-[#0a0f0d] border-r border-[#141f1b] flex-col fixed inset-y-0 z-30">
          {SidebarBody}
        </aside>

        {/* Mobile drawer */}
        {drawerOpen && (
          <div className="lg:hidden fixed inset-0 z-50" onClick={() => setDrawerOpen(false)}>
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"/>
            <aside onClick={e => e.stopPropagation()} className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-[#0a0f0d] border-r border-[#141f1b] flex flex-col shadow-2xl">
              {SidebarBody}
            </aside>
          </div>
        )}

        <main className="flex-1 lg:ml-64 min-w-0">
          {/* Mobile top bar */}
          <header className="lg:hidden sticky top-0 z-30 bg-[#050807]/85 backdrop-blur-xl border-b border-[#141f1b] px-4 py-3 flex items-center gap-3">
            <button
              onClick={() => setDrawerOpen(true)}
              data-testid="open-admin-sidebar-btn"
              className="w-9 h-9 rounded-lg border border-[#1a2823] flex items-center justify-center text-gray-300 hover:text-[#00e676] hover:border-[#00e676]"
            >
              <Menu size={16}/>
            </button>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded bg-[#00e676] flex items-center justify-center">
                <Shield size={13} className="text-black"/>
              </div>
              <div>
                <div className="text-xs font-extrabold text-white whitespace-nowrap">NEXUS FX <span className="text-[#00e676]">PRO</span> Admin</div>
              </div>
            </div>
          </header>
          <div className="p-4 sm:p-6 lg:p-8">
            <Outlet/>
          </div>
        </main>
      </div>
    </AdminGate>
  );
};

export default AdminLayout;
